import { useMutation } from "@apollo/client";
import { createContext, Dispatch, useEffect, useMemo, useReducer, useState } from "react";

import { WordopolisConfiguration } from "../gql/graphql";
import { IntroModal } from "../components/WordopolisGame/Modals/IntroModal";
import { NicknameModal } from "../components/shared/NicknameModal";
import { RESPONSE_CREATE } from "../graphql/mutations/Response";
import { TimeProvider } from "./time";
import { WORDOPOLIS_STAGE_SIZE } from "../constants";
import { WordopolisAction, WordopolisActionKind, wordopolisReducer, WordopolisState } from "../reducers/wordopolis";
import { FinishGameModal } from "../components/WordopolisGame/Modals/FinishGameModal";
import { WordopolisGame } from "../graphql/helpers/isWordopolisGame";
import { useGlobalConfiguration } from "../hooks/useGlobalConfiugration";

export const WordopolisContext = createContext<[WordopolisState, Dispatch<WordopolisAction>] | null>(null);

export interface WordopolisContextProviderProps {
  children: React.ReactNode;
  finishGame: (newResponseId?: string) => void;
  game: WordopolisGame;
}

export const WordopolisContextProvider = ({ children, finishGame, game }: WordopolisContextProviderProps) => {
  const { numberOfColumns, numberOfRows, gameVisuals } = game.configuration as WordopolisConfiguration;
  const [resultId, setResultId] = useState<string | undefined>(undefined);
  const { projectNickname } = useGlobalConfiguration();

  const [saveWordopolisResult] = useMutation(RESPONSE_CREATE);

  const stageSize = useMemo(() => {
    const longerSide = Math.max(numberOfColumns, numberOfRows);

    return {
      width: Math.floor((numberOfColumns / longerSide) * WORDOPOLIS_STAGE_SIZE),
      height: Math.floor((numberOfRows / longerSide) * WORDOPOLIS_STAGE_SIZE),
    };
  }, [numberOfColumns, numberOfRows]);

  const reducer = useReducer(wordopolisReducer, {
    configuration: {
      ...(game.configuration as WordopolisConfiguration),
      words: game.configuration.words.map((word) => {
        const attribute = game.configuration.attributes.find(({ name }) => name === word.value);

        return {
          ...word,
          description: attribute?.customText ?? undefined,
        };
      }),
    },
    status: {
      foundWords: [],
      isGameFinished: false,
      startTime: Date.now(),
      showWords: false,
      isIntroFinished: false,
      toRate: [],
    },
    stage: {
      ...stageSize,
    },
    results: {},
  });

  const [
    {
      configuration: { showLeaderboard, limitedTime, isTimeLimited, leaderboardConfiguration },
      status: { isGameFinished, nickname, toRate, startTime, isIntroFinished, showWords, finishGameTime },
      results,
    },
    dispatch,
  ] = reducer;
  const isLeaderboardVisible = showLeaderboard && !nickname && !projectNickname;

  useEffect(() => {
    if (toRate.length || !isGameFinished) {
      return;
    }

    let gameDuration = finishGameTime;

    if (gameDuration === undefined) {
      gameDuration = Object.values(results).reduce((acc, { time }) => (time > acc ? time : acc), 0);
    }

    saveWordopolisResult({
      variables: {
        data: {
          wordopolisScores: Object.values(results),
          gameId: game.id,
          nickname: projectNickname || nickname,
          gameDuration,
          endTime: new Date().toISOString(),
          startTime: new Date(startTime).toISOString(),
        },
      },
      onCompleted: ({ responseCreate: { slug } }) => {
        setResultId(String(slug));
      },
    });
  }, [toRate, isGameFinished]);

  return (
    <WordopolisContext.Provider value={reducer}>
      <NicknameModal
        isOpen={isLeaderboardVisible}
        gameId={game.id}
        themeColor={gameVisuals?.themeColor}
        configuration={leaderboardConfiguration}
        onContinue={(nickname) =>
          dispatch({
            type: WordopolisActionKind.SET_NICKNAME,
            payload: { nickname },
          })
        }
      />
      <FinishGameModal onFinish={() => finishGame(resultId)} />
      {!isLeaderboardVisible && <IntroModal />}
      <TimeProvider
        time={isTimeLimited ? limitedTime * 1000 : Infinity}
        isPaused={isGameFinished || !isIntroFinished || Boolean(toRate.length) || showWords}
        onTimeEnd={() => dispatch({ type: WordopolisActionKind.END_GAME, payload: { time: limitedTime * 1000 } })}
      >
        {children}
      </TimeProvider>
    </WordopolisContext.Provider>
  );
};
